import classnames from 'classnames/bind'
import React, { useEffect, useRef } from 'react'

import {
  useIsomorphicLayoutEffect,
  useMeasure,
  useWindowSize,
} from '@unlikelystudio/react-hooks'

import { useTheme } from '~/providers/ThemeProvider'

import './curve'
import css from './styles.module.scss'

const cx = classnames.bind(css)

// XY values
const TYPES = {
  header: {
    mobile: [1 / 4 + 0.08, 0, 1 / 4, 1 / 3, 0.8, 0.64, 1, 0.999, 1, 1, 1, 0],
    desktop: [0.58, 0, 0.5, 1 / 3, 0.8, 0.64, 1, 0.999, 1, 1, 1, 0],
  },
  component: {
    mobile: [0, 0.15, 0, 0.15, 1, 0.15, 1, 1, 0, 1],
    desktop: [
      0, 0.12, 0, 0.12, 0.2, 0.1, 0.5, 0.035, 0.8, 0.01, 1, 0.15, 1.1, 0.5, 1.2,
      0.05, 1, 1, 0, 1,
    ],
  },
  footer: {
    mobile: [0, 0.1, 0.05, 0.05, 1, 0.25, 1, 1, 0, 1],
    desktop: [0, 0.2, 0.6, 0.02, 1, 0.3, 1, 1, 0, 1],
  },
}

export interface CurveProps {
  className?: string
  type?: 'header' | 'component' | 'footer'
  color?: string
  hasGradiant?: boolean
}
/**
 * Curve drawing component
 * @param props
 * @returns Component
 */
function Curve({
  className,
  type = 'component',
  color = '#f1eeff',
  hasGradiant = true,
}: CurveProps) {
  const canvasRef = useRef(null)
  const contextRef = useRef(null)
  const componentRef = useRef(null)
  const bounds = useMeasure(componentRef)
  const size = useWindowSize()
  const { screens } = useTheme()

  const [lg] = screens?.lg?.split('px')

  let pts =
    TYPES?.[type][
      lg ? (size?.width < parseInt(lg) ? 'mobile' : 'desktop') : 'desktop'
    ]

  useIsomorphicLayoutEffect(() => {
    contextRef.current = canvasRef?.current?.getContext('2d')
    drawCurve()
  }, [])

  useIsomorphicLayoutEffect(() => {
    if (canvasRef?.current) {
      canvasRef.current.width = bounds.width
      canvasRef.current.height = bounds.height
      drawCurve()
    }
  }, [{ ...bounds }, canvasRef.current])

  /**
   * Debug drawing points
   * @param points
   */
  function debug(points) {
    for (var i = 0; i < points.length; i += 2) {
      if (i === 0) contextRef.current.strokeStyle = '#0f0'
      contextRef.current.strokeStyle = 'rgba(255,0,0,0.7)'
      contextRef.current.strokeRect(points[i] - 2, points[i + 1] - 2, 4, 4)
    }
  }

  /**
   * Draw curve from points
   */
  function drawCurve() {
    if (!contextRef?.current) return
    const w = canvasRef.current.width
    const h = canvasRef.current.height

    let gradient

    if (type === 'header') {
      gradient = contextRef.current.createLinearGradient(0, 0, bounds.width, 0)
    } else if (type === 'component') {
      gradient = contextRef.current.createLinearGradient(0, 0, 0, bounds.height)
    }

    if (hasGradiant) {
      gradient.addColorStop(0, color)
      gradient.addColorStop(1, 'white')
    }

    contextRef.current.clearRect(0, 0, w, h)
    const processedPoints = pts.map((pt, index) =>
      index % 2 === 0 ? pt * w : pt * h,
    )
    contextRef.current.beginPath()
    contextRef.current.moveTo(processedPoints[0], processedPoints[1])
    contextRef.current.curve(processedPoints, 0.5, 50, true)
    contextRef.current.fillStyle = hasGradiant ? gradient : color
    contextRef.current.fill()

    // debug(processedPoints)
  }

  return (
    <div className={cx(css.Curve, className)} ref={componentRef}>
      <canvas ref={canvasRef} />
    </div>
  )
}

Curve.defaultProps = {}

export default Curve
